import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Aktiviteter() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    
    // Update page title and meta description
    document.title = 'Aktiviteter - Easy Accounting AS';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Delta på våre aktiviteter og kurs innen regnskap og økonomi. Workshops, seminarer og nettverksmøter for bedriftseiere.');
    }
  }, []);

  const upcomingEvents = [
    {
      date: '5. desember',
      title: 'Nettverkmøte for fagfolk i regnskap',
      time: '10:00 - 16:00',
      type: 'Konferanse'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-r from-green-600 to-green-800 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-20 h-20 bg-white/10 rounded-full animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}></div>
          <div className="absolute bottom-20 right-20 w-16 h-16 bg-white/10 rounded-full animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}></div>
        </div>
        <div className={`mx-auto max-w-7xl px-6 lg:px-8 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-6 animate-fade-in-up">
            Aktiviteter og kurs
          </h1>
          <p className="text-xl text-green-100 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Delta på våre kurs, workshops og nettverksmøter for å utvikle dine ferdigheter innen regnskap og økonomi
          </p>
        </div>
      </section>

      {/* Activities Grid */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
              Kommende aktiviteter
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Meld deg på våre profesjonelle kurs og aktiviteter
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 justify-center">
            {/* Conference card */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden max-w-md mx-auto">
              <img 
                src="https://public.readdy.ai/ai/img_res/48623412bdfad07a6e78f62c0aee4503.jpg"
                alt="Regnskapskonferanse"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <span className="inline-block bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full mb-3">
                  Spesialarrangement
                </span>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Nettverkmøte for fagfolk i regnskap
                </h3>
                <p className="text-gray-600 mb-4">
                  En heldagskonferanse spesielt tilpasset små regnskapskontor og enkeltpraktikere. 
                  Fokus på praktiske løsninger, nye regelverk og effektiv drift.
                </p>
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center">
                    <i className="ri-calendar-line mr-1"></i>
                    5. desember 2025
                  </div>
                  <div className="flex items-center">
                    <i className="ri-map-pin-line mr-1"></i>
                    Lørenskog
                  </div>
                </div>
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Programmet inkluderer:</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                      Skape nettverk
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                      Kvalitetskontroll i praksis
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                      Vekst i bransjen
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                      Teknologi som forenkler hverdagen
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                      Paneldebatt og erfaringsutveksling
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-500 mr-2 mt-0.5"></i>
                      Lunsj og drikke inkludert
                    </li>
                  </ul>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">900 kr</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-8 animate-fade-in-up">
                Hvorfor delta på våre aktiviteter?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                  <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg mb-4">
                    <i className="ri-brain-line text-2xl text-green-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Faglig utvikling</h3>
                  <p className="text-gray-600">Hold deg oppdatert på de nyeste trendene og metodene innen regnskap og økonomi.</p>
                </div>
                <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg mb-4">
                    <i className="ri-team-line text-2xl text-green-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Nettverk</h3>
                  <p className="text-gray-600">Møt andre fagfolk og bygg verdifulle kontakter i bransjen.</p>
                </div>
                <div className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                  <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg mb-4">
                    <i className="ri-tools-line text-2xl text-green-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Praktiske verktøy</h3>
                  <p className="text-gray-600">Få tilgang til maler, verktøy og ressurser som kan brukes direkte i arbeidet.</p>
                </div>
                <div className="animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                  <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg mb-4">
                    <i className="ri-certificate-line text-2xl text-green-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Sertifisering</h3>
                  <p className="text-gray-600">Mange av våre kurs gir sertifikater som dokumenterer din kompetanse.</p>
                </div>
              </div>
            </div>
            
            <div className="animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Kommende arrangementer</h3>
                <div className="space-y-4">
                  {upcomingEvents.map((event, index) => (
                    <div key={index} className="border-l-4 border-green-500 pl-4 py-2">
                      <div className="text-sm text-green-600 font-medium">{event.date}</div>
                      <div className="font-medium text-gray-900">{event.title}</div>
                      <div className="text-sm text-gray-600">{event.time} • {event.type}</div>
                    </div>
                  ))}
                </div>
                <Link to="/kontakt" className="block mt-6">
                  <Button className="w-full hover:scale-105 transition-transform duration-300">
                    Se alle arrangementer
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 bg-green-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-6 animate-fade-in-up">
            Få beskjed om nye aktiviteter
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Meld deg på vårt nyhetsbrev og få informasjon om nye kurs og arrangementer direkte i innboksen.
          </p>
          <div className="flex flex-col sm-flex-row justify-center items-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <Link to="/nyhetsbrev">
              <Button size="lg" className="hover:scale-105 transition-transform duration-300">
                <i className="ri-mail-line mr-2"></i>
                Meld deg på nyhetsbrev
              </Button>
            </Link>
            <Link to="/kontakt">
              <Button variant="outline" size="lg" className="hover:scale-105 transition-transform duration-300">
                <i className="ri-phone-line mr-2"></i>
                Kontakt oss
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
